import { useCallback, useRef, useState } from "react";

export const MIN_COLUMN = 300;
const MIN = MIN_COLUMN;
// Wide enough to read a long answer without the split disappearing entirely —
// how the width is spent between the two panes is the reader's call.
const MAX = 800;
const KEYBOARD_STEP = 24;

const clamp = (value: number) =>
  Math.min(MAX, Math.max(MIN, Math.round(value)));

const read = (key: string, fallback: number) => {
  try {
    const stored = Number(localStorage.getItem(key));
    return Number.isFinite(stored) && stored > 0 ? clamp(stored) : fallback;
  } catch {
    return fallback;
  }
};

const write = (key: string, value: number) => {
  try {
    localStorage.setItem(key, String(value));
  } catch {
    // A remembered column width is a convenience, never a requirement.
  }
};

/**
 * A draggable column width for the workspace split, remembered per viewer.
 *
 * Pointer capture means the drag survives the cursor leaving the handle, and
 * the separator is operable from the keyboard — a split people work in all day
 * should not need a mouse.
 */
export const useResizableColumn = (storageKey: string, initial: number) => {
  const [width, setWidth] = useState(() => read(storageKey, initial));
  const [dragging, setDragging] = useState(false);
  const origin = useRef({ x: 0, width: 0 });

  const commit = useCallback(
    (next: number) => {
      const value = clamp(next);
      setWidth(value);
      write(storageKey, value);
    },
    [storageKey],
  );

  const separatorProps = {
    role: "separator" as const,
    "aria-orientation": "vertical" as const,
    "aria-label": "Resize the conversation",
    "aria-valuenow": width,
    "aria-valuemin": MIN,
    "aria-valuemax": MAX,
    tabIndex: 0,
    onPointerDown: (event: React.PointerEvent<HTMLDivElement>) => {
      event.currentTarget.setPointerCapture(event.pointerId);
      origin.current = { x: event.clientX, width };
      setDragging(true);
    },
    onPointerMove: (event: React.PointerEvent<HTMLDivElement>) => {
      if (!dragging) return;
      commit(origin.current.width + (event.clientX - origin.current.x));
    },
    onPointerUp: (event: React.PointerEvent<HTMLDivElement>) => {
      event.currentTarget.releasePointerCapture(event.pointerId);
      setDragging(false);
    },
    onPointerCancel: () => setDragging(false),
    onLostPointerCapture: () => setDragging(false),
    onDoubleClick: () => commit(initial),
    onKeyDown: (event: React.KeyboardEvent<HTMLDivElement>) => {
      const step =
        event.key === "ArrowLeft"
          ? -KEYBOARD_STEP
          : event.key === "ArrowRight"
            ? KEYBOARD_STEP
            : 0;
      if (!step) return;
      event.preventDefault();
      commit(width + step);
    },
  };

  return { width, dragging, separatorProps };
};
